import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faCalendarAlt, faSave, faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import DaisyDatePicker from './DaisyDatePicker'

interface PTORequestModalProps {
  isOpen: boolean
  onClose: () => void
}

const PTORequestModal: React.FC<PTORequestModalProps> = ({ isOpen, onClose }) => {
  // Mock hire date for demo
  const hireDate = new Date('2021-01-01')
  const today = new Date()

  const [formData, setFormData] = useState({
    startDate: '',
    endDate: '',
    totalHours: '',
    leaveType: 'vacation',
    reason: ''
  })

  // Calculate years of service
  const yearsOfService = (formData.startDate ? new Date(formData.startDate) : today).getFullYear() - hireDate.getFullYear() + (((formData.startDate ? new Date(formData.startDate) : today).getMonth() - hireDate.getMonth())/12)

  // Determine accrual rate
  let accrualPerYear = 56
  if (yearsOfService >= 2 && yearsOfService < 5) accrualPerYear = 96
  if (yearsOfService >= 5) accrualPerYear = 136

  // Calculate projected PTO balance
  const currentPTO = 40 // mock current PTO balance in hours
  let projectedPTO = currentPTO
  if (formData.startDate) {
    const start = new Date(formData.startDate)
    if (start > today) {
      const weeks = Math.floor((start.getTime() - today.getTime()) / (1000 * 60 * 60 * 24 * 7))
      const accrualPerWeek = accrualPerYear / 52
      projectedPTO = currentPTO + weeks * accrualPerWeek
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // TODO: Submit PTO request to API
    console.log('PTO Request submitted:', formData)
    onClose()
    // Reset form
    setFormData({
      startDate: '',
      endDate: '',
      totalHours: '',
      leaveType: 'vacation',
      reason: ''
    })
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  if (!isOpen) return null

  return (
    <div className="modal modal-open">
      <div className="modal-box w-11/12 max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-bold text-xl flex items-center gap-2">
              <FontAwesomeIcon icon={faCalendarAlt} />
              Request Time Off
            </h3>
            <p className="text-sm text-base-content/60">Submit a request for paid time off</p>
          </div>
          <button
            onClick={onClose}
            className="btn btn-sm btn-circle btn-ghost"
            aria-label="Close modal"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Date Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">
                <span className="label-text font-semibold">Start Date</span>
              </label>
              <DaisyDatePicker
                className="w-full"
                value={formData.startDate}
                onChange={(value) => handleInputChange('startDate', value)}
                aria-label="PTO start date"
              />
            </div>
            <div>
              <label className="label">
                <span className="label-text font-semibold">End Date</span>
              </label>
              <DaisyDatePicker
                className="w-full"
                value={formData.endDate}
                onChange={(value) => handleInputChange('endDate', value)}
                aria-label="PTO end date"
              />
            </div>
          </div>

          {/* Total Hours Requested */}
          <div>
            <label className="label">
              <span className="label-text font-semibold">Total Hours Requested</span>
            </label>
            <input
              type="number"
              className="input input-bordered w-full"
              value={formData.totalHours}
              onChange={(e) => handleInputChange('totalHours', e.target.value)}
              required
              min={1}
              max={80}
            />
            {formData.totalHours && Number(formData.totalHours) > projectedPTO && (
              <div className="mt-2 text-warning text-sm flex items-center gap-2">
                <FontAwesomeIcon icon={faInfoCircle} />
                You are requesting more hours than you are projected to have available. You can still submit the request, but it will require additional manager review and approval.
              </div>
            )}
          </div>

          {/* Leave Type */}
          <div>
            <label className="label">
              <span className="label-text font-semibold">Type of Leave</span>
            </label>
            <select
              className="select select-bordered w-full"
              value={formData.leaveType}
              onChange={(e) => handleInputChange('leaveType', e.target.value)}
              required
            >
              <option value="vacation">Vacation</option>
              <option value="sick">Sick Leave</option>
              <option value="personal">Personal Time</option>
              <option value="bereavement">Bereavement</option>
              <option value="jury">Jury Duty</option>
              <option value="military">Military Leave</option>
            </select>
          </div>

          {/* Reason (optional) */}
          <div>
            <label className="label">
              <span className="label-text font-semibold">Reason for Leave <span className="text-xs text-base-content/60">(optional)</span></span>
            </label>
            <textarea
              className="textarea textarea-bordered w-full"
              rows={3}
              placeholder="Provide a reason (optional)"
              value={formData.reason}
              onChange={(e) => handleInputChange('reason', e.target.value)}
            />
          </div>

          {/* PTO Balance Info & Projection */}
          <div className="bg-base-200 p-4 rounded-lg flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold mb-0">PTO Balance</h4>
              <div className="tooltip tooltip-bottom" data-tip="PTO accrues by hour worked. 0-2 years: 56h/year, 2-5 years: 96h/year, 5+ years: 136h/year. Accrual assumes 40h/week.">
                <FontAwesomeIcon icon={faInfoCircle} className="text-base-content/60 cursor-help" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-base-content/60">Current:</span>
                <span className="ml-2 font-semibold text-success">{currentPTO.toFixed(1)} hours</span>
              </div>
              <div>
                <span className="text-base-content/60">Projected by start date:</span>
                <span className="ml-2 font-semibold text-info">{projectedPTO.toFixed(1)} hours</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="modal-action">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              <FontAwesomeIcon icon={faSave} className="mr-2" />
              Submit Request
            </button>
          </div>
        </form>
      </div>
      
      {/* Backdrop */}
      <div className="modal-backdrop" onClick={onClose}></div>
    </div>
  )
}

export default PTORequestModal 